<script lang="ts">
	import { Checkbox, Input, Radio } from "flowbite-svelte-v1";
	import { ChoiceType, FieldPropertiesSchema, FieldType, InputType, type Field } from "#gen/forms/v1/field_pb.js";
	import P from "#lib/components/P.svelte";
	import MultiFieldDateInput from "#lib/components/page/components/form/MultiFieldDateInput.svelte";
	import FeatureFlagGate from "#lib/permissions/FeatureFlagGate.svelte";
	import { create } from "#lib/protobuf-compat.js";

	interface Props {
		field: Field;
		disabled?: boolean;
		onChange?: (field: Field) => void;
	}

	let { field, disabled = false, onChange }: Props = $props();

	const group = $derived(field.properties?.properties.case === "date" ? field.properties?.properties.value.inputType : InputType.SINGLE);
	const captureImage = $derived(field.properties?.properties.case === "fileUpload" ? Boolean(field.properties.properties.value.captureImage) : false);
	const searchable = $derived(field.properties?.properties.case === "choice" ? Boolean(field.properties.properties.value.searchable) : false);

	function handleDateValueChange(e: Event) {
		const target = e.target as HTMLInputElement;
		const value = Number.parseInt(target.value) as InputType;
		if (Number.isNaN(value)) {
			return;
		}
		if (!field.properties) {
			field.properties = create(FieldPropertiesSchema, {
				properties: {
					case: "date",
					value: {
						inputType: InputType.SINGLE,
					},
				},
			});
		}
		const properties = field.properties?.properties.case === "date" ? field.properties?.properties.value : undefined;
		if (properties) {
			properties.inputType = value;
			onChange?.(field);
		}
	}

	function handleFileUploadValueChange(e: Event) {
		if (field?.fieldType !== FieldType.FILE_UPLOAD) return;
		if (field?.properties === undefined) {
			field.properties = create(FieldPropertiesSchema, {
				properties: {
					case: "fileUpload",
					value: {
						captureImage: false,
					},
				},
			});
		}

		const checked = (e.currentTarget as HTMLInputElement).checked;
		const properties = field.properties.properties.case === "fileUpload" ? field.properties.properties.value : undefined;

		if (properties) {
			properties.captureImage = checked;
			onChange?.(field);
		}
	}

	function handleChoiceValueChange(e: Event) {
		if (field?.fieldType !== FieldType.CHOICE) return;
		if (field?.properties === undefined) {
			field.properties = create(FieldPropertiesSchema, {
				properties: {
					case: "choice",
					value: {
						choiceType: ChoiceType.DROPDOWN,
						searchable: false,
					},
				},
			});
		}

		const checked = (e.currentTarget as HTMLInputElement).checked;
		const properties = field.properties.properties.case === "choice" ? field.properties.properties.value : undefined;

		if (properties) {
			properties.searchable = checked;
			onChange?.(field);
		}
	}
</script>

{#if field.fieldType === FieldType.DATE}
	<div class="flex flex-col">
		<P size="sm" weight="normal" class=" text-gray-900">Field type:</P>
		<div class="grid w-full grid-cols-2 gap-4 py-2">
			<Radio {disabled} name="inputType" {group} checked custom value={InputType.SINGLE} onchange={handleDateValueChange}>
				<div class="peer-checked:border-primary-600 flex w-full cursor-pointer flex-col items-center justify-between gap-4 rounded-lg border border-gray-200 bg-white px-6 py-4 text-gray-500 hover:bg-gray-100 hover:text-gray-600">
					<Input id="{field.name}-choice-single" class="date-disabled pointer-events-none" type="date" />
					<div class="w-full text-center text-sm font-medium">Single</div>
				</div>
			</Radio>
			<Radio {disabled} name="inputType" {group} custom value={InputType.MULTIPLE} onchange={handleDateValueChange}>
				<div class="peer-checked:border-primary-600 flex w-full cursor-pointer flex-col items-center justify-between gap-4 rounded-lg border border-gray-200 bg-white px-6 py-4 text-gray-500 hover:bg-gray-100 hover:text-gray-600">
					<MultiFieldDateInput id="{field.name}-choice-multiple" class="pointer-events-none" />
					<div class="w-full text-center text-sm font-medium">Multiple</div>
				</div>
			</Radio>
		</div>
	</div>
{:else if field.fieldType === FieldType.FILE_UPLOAD}
	<div class="flex flex-col">
		<Checkbox {disabled} checked={captureImage} onchange={handleFileUploadValueChange}>Have users capture a photo using their camera</Checkbox>
	</div>
{:else if field.fieldType === FieldType.CHOICE && field.properties?.properties.case === "choice" && field.properties.properties.value.choiceType === ChoiceType.DROPDOWN}
	<FeatureFlagGate flag="choice_field_searchable">
		<div class="flex flex-col">
			<Checkbox {disabled} checked={searchable} onchange={handleChoiceValueChange}>Allow users to search dropdown options</Checkbox>
		</div>
	</FeatureFlagGate>
{/if}
